import { useEffect, useState } from "react";
import { ChevronDown, ChevronRight, ClipboardPlus, FileCheck2, Paperclip, X } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export const EVALUATION_TYPES = ["Speech-Language", "OT", "PT", "Psychological", "Educational", "FBA", "Screening", "Other"];
export const NOTES_PLACEHOLDER = "Anything the evaluator should know: reason for referral, eligibility details, teacher and parent contacts.";
const CONSENT_ACCEPT = ".pdf,.png,.jpg,.jpeg,.webp,application/pdf,image/png,image/jpeg,image/webp";

const emptyForm = {
  client_id: "",
  student_name: "",
  date_of_birth: "",
  grade: "",
  evaluation_type: "",
  rate_item_id: "",
  service_kind: "",
  due_date: "",
  meeting_date: "",
  due_date_override_note: "",
  evaluation_request: "",
  consent_not_signed: false,
  key_considerations: "",
  disability: "",
  other_involvement: "",
  teacher_contacts: "",
  parent_contact: "",
  consent_signed_date: "",
};
const MORE_DETAIL_FIELDS = ["key_considerations", "disability", "other_involvement", "teacher_contacts", "parent_contact", "consent_signed_date"];

function initialForm(initial) {
  if (!initial) return emptyForm;
  return Object.fromEntries(Object.keys(emptyForm).map((key) => [key, key === "consent_not_signed" ? Boolean(initial[key]) : initial[key] || ""]));
}

const meetingDue = (meeting) => { const d = new Date(`${meeting}T12:00:00`); d.setDate(d.getDate() - 7); return d.toISOString().slice(0, 10); };

const FieldLabel = ({ label, optional }) => (
  <span className="field-label">
    {label}
    {optional && <em className="field-optional">· optional</em>}
  </span>
);

const Field = ({ label, name, form, onChange, prefix, type = "text", multiline = false, optional = false, full = false, placeholder = "", rows = "2" }) => (
  <label className={full ? "drawer-form-full" : ""}>
    <FieldLabel label={label} optional={optional} />
    {multiline ? (
      <textarea data-testid={`${prefix}-${name}`} placeholder={placeholder} required={!optional} rows={rows} value={form[name]} onChange={(event) => onChange(name, event.target.value)} />
    ) : (
      <input data-testid={`${prefix}-${name}`} placeholder={placeholder} required={!optional} type={type} value={form[name]} onChange={(event) => onChange(name, event.target.value)} />
    )}
  </label>
);

const DISCIPLINES = EVALUATION_TYPES.filter((type) => type !== "Screening");

export const EvaluationRequestForm = ({ mode = "staff", clientOptions = [], initial = null, onClose, onCreated }) => {
  const prefix = mode === "client" ? "client-evaluation" : "staff-evaluation";
  const editing = Boolean(initial);
  const [form, setForm] = useState(() => initialForm(initial));
  const [disciplines, setDisciplines] = useState(() => (initial?.evaluation_type ? [initial.evaluation_type] : []));
  const [customRate, setCustomRate] = useState({ custom_name: "", client_fee: "", provider_fee: "", save_to_rate_list: false });
  const { user } = useAuth();
  const canSeeFees = ["system_administrator", "executive"].includes(user?.role);
  const multi = !editing && disciplines.length > 1;
  const [consentFile, setConsentFile] = useState(null);
  const [consent, setConsent] = useState(() => ({
    method: initial?.consent?.method || (initial?.consent_document ? "signed_form" : initial?.consent_not_signed ? "not_yet" : ""),
    given_by: initial?.consent?.given_by || "",
    date: initial?.consent?.date || "",
    note: initial?.consent?.note || "",
  }));
  const [showMore, setShowMore] = useState(() => Boolean(initial && MORE_DETAIL_FIELDS.some((key) => initial[key])));
  const [error, setError] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const onChange = (name, value) => setForm((current) => ({ ...current, [name]: value }));
  const [typeOptions, setTypeOptions] = useState([]);
  const typeClientId = mode === "client" ? "own" : form.client_id;
  useEffect(() => {
    let active = true;
    if (!typeClientId) {
      setTypeOptions([]);
      return undefined;
    }
    api.get("/client-evaluation-requests/type-options", { params: mode === "client" ? {} : { client_id: form.client_id } })
      .then((response) => active && setTypeOptions(response.data))
      .catch(() => active && setTypeOptions([]));
    return () => {
      active = false;
    };
  }, [typeClientId, form.client_id, mode]);
  const pickClient = (value) => setForm((current) => ({ ...current, client_id: value, rate_item_id: "" }));
  const typeChoices = typeOptions.filter((option) => disciplines.length === 1 && option.evaluation_types.includes(disciplines[0]) && option.evaluation_types.includes("Screening") === (form.service_kind === "Screening"));
  const toggleDiscipline = (type, checked) => {
    setDisciplines((current) => {
      const next = editing ? [type] : checked ? [...current, type] : current.filter((item) => item !== type);
      setForm((state) => ({ ...state, evaluation_type: next[0] || "", rate_item_id: state.rate_item_id === "custom" ? "custom" : "" }));
      return next;
    });
  };
  const pickType = (value) => {
    if (value === "custom") {
      setForm((current) => ({ ...current, rate_item_id: "custom" }));
    } else {
      setForm((current) => ({ ...current, rate_item_id: value.startsWith("item:") ? value.slice(5) : "" }));
    }
  };
  useEffect(() => {
    if (editing || multi || form.rate_item_id === "custom") return;
    if (typeChoices.length === 1 && form.rate_item_id !== typeChoices[0].id) setForm((current) => ({ ...current, rate_item_id: typeChoices[0].id }));
    else if (form.rate_item_id && !typeChoices.some((option) => option.id === form.rate_item_id)) setForm((current) => ({ ...current, rate_item_id: "" }));
  }, [typeChoices, editing, multi, form.rate_item_id]);
  const typeValue = form.rate_item_id === "custom" ? "custom" : form.rate_item_id ? `item:${form.rate_item_id}` : "";
  const options =
    editing && initial.client_id && !clientOptions.some((client) => client.id === initial.client_id)
      ? [{ id: initial.client_id, name: initial.client_name }, ...clientOptions]
      : clientOptions;
  const existingConsent = initial?.consent_document;

  async function submit(event) {
    event.preventDefault();
    if (!consent.method) {
      setError("Choose how consent was given: Signed form, Verbal from school or Not yet.");
      return;
    }
    if (consent.method === "signed_form" && !consentFile && !existingConsent) {
      setError("Upload the signed consent form.");
      return;
    }
    if (consent.method === "verbal" && (!consent.given_by.trim() || !consent.date)) {
      setError("Verbal consent needs who gave it and the date.");
      return;
    }
    if (!form.service_kind) {
      setError("Choose the service kind: Evaluation, Screening or Other.");
      return;
    }
    if (disciplines.length === 0) {
      setError("Choose at least one discipline.");
      return;
    }
    if (!multi && !form.rate_item_id) {
      setError("Choose the type from this client’s rate list, or Custom rate…");
      return;
    }
    if (form.rate_item_id === "custom" && !customRate.custom_name.trim()) {
      setError("Give the custom service a name.");
      return;
    }
    setIsSaving(true);
    setError("");
    try {
      const consentFields = { consent_method: consent.method, consent_given_by: consent.given_by.trim(), consent_date: consent.date, consent_note: consent.note.trim(), consent_not_signed: consent.method === "not_yet" };
      const base = mode === "client" ? { ...form, ...consentFields, client_id: "" } : { ...form, ...consentFields };
      const payload = multi ? { ...base, disciplines, rate_item_id: "", evaluation_type: disciplines[0] } : form.rate_item_id === "custom" ? { ...base, rate_item_id: "", custom_name: customRate.custom_name.trim() } : base;
      const response = editing
        ? await api.put(`/client-evaluation-requests/${initial.id}`, payload)
        : await api.post("/client-evaluation-requests", payload);
      let record = response.data;
      if (!editing && form.rate_item_id === "custom" && canSeeFees) {
        const fee = customRate.client_fee === "" ? null : Number(customRate.client_fee);
        const providerFee = customRate.provider_fee === "" ? (fee === null ? null : Math.round(fee * 0.7)) : Number(customRate.provider_fee);
        const ids = [record.id, ...(record.linked_ids || [])];
        for (const id of ids) {
          // eslint-disable-next-line no-await-in-loop
          await api.post(`/evaluation-pipeline/${id}/rate`, { rate_item_id: "custom", custom_name: customRate.custom_name.trim(), client_fee: fee, provider_fee: providerFee, save_to_rate_list: customRate.save_to_rate_list });
        }
      }
      if (editing && mode === "staff" && consent.method !== "signed_form" && (consent.method !== (initial.consent?.method || (initial.consent_not_signed ? "not_yet" : "")) || consent.given_by !== (initial.consent?.given_by || ""))) {
        await api.post(`/evaluation-pipeline/${record.id}/consent`, { method: consent.method, given_by: consent.given_by.trim(), date: consent.date, note: consent.note.trim() });
      }
      if (consentFile) {
        const body = new FormData();
        body.append("consent_file", consentFile);
        const upload = await api.post(`/client-evaluation-requests/${record.id}/consent`, body, { headers: { "Content-Type": "multipart/form-data" } });
        record = { ...record, consent_document: upload.data, consent_not_signed: false };
      }
      onCreated(record);
    } catch (requestError) {
      setError(formatApiError(requestError));
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <section className={`inline-edit-panel staff-evaluation-panel ${mode === "client" ? "client-evaluation-panel" : ""}`} data-testid={`${prefix}-form-panel`}>
      <div className="section-heading">
        <div>
          <h2>{editing ? "Edit evaluation request" : mode === "client" ? "Request a student evaluation" : "Log an evaluation request"}</h2>
          <p>
            {editing
              ? `${initial.student_name} · ${initial.client_name}. Changes are recorded in the request history.`
              : mode === "client"
                ? "Only the essentials are required. TAG will follow up if anything else is needed."
                : "Enter a request on behalf of a client. It enters the review queue exactly as a client submission would."}
          </p>
        </div>
        {onClose && (
          <button aria-label="Close evaluation form" className="projection-icon-button" data-testid={`close-${prefix}-form`} type="button" onClick={onClose}>
            <X size={16} />
          </button>
        )}
      </div>
      {error && <p className="form-message error-message" data-testid={`${prefix}-error`}>{error}</p>}
      <form className="drawer-form-grid staff-evaluation-form" data-testid={`${prefix}-form`} onSubmit={submit}>
        {mode === "staff" && (
          <label className="drawer-form-full">
            <FieldLabel label="Client" />
            <select data-testid={`${prefix}-client_id`} required value={form.client_id} onChange={(event) => pickClient(event.target.value)}>
              <option value="">Select the client this evaluation is for</option>
              {options.map((client) => <option key={client.id} value={client.id}>{client.name}</option>)}
            </select>
          </label>
        )}
        <Field label="Student name" name="student_name" placeholder="First and last name" form={form} onChange={onChange} prefix={prefix} />
        <Field label="Date of birth" name="date_of_birth" type="date" optional={editing} form={form} onChange={onChange} prefix={prefix} />
        <Field label="Grade" name="grade" placeholder="e.g. 3" optional form={form} onChange={onChange} prefix={prefix} />
        <fieldset className="service-kind-field drawer-form-full" data-testid={`${prefix}-service-kind`}>
          <FieldLabel label="Service kind" />
          <div className="service-kind-options">
            {["Evaluation", "Screening", "Other"].map((kind) => (
              <label key={kind} className={`service-kind-option ${form.service_kind === kind ? "selected" : ""}`}>
                <input data-testid={`${prefix}-service-kind-${kind.toLowerCase()}`} type="radio" name={`${prefix}-service-kind`} value={kind} checked={form.service_kind === kind} onChange={() => setForm((current) => ({ ...current, service_kind: kind, rate_item_id: current.rate_item_id === "custom" ? "custom" : "" }))} />
                {kind}
              </label>
            ))}
          </div>
        </fieldset>
        <fieldset className="discipline-multi drawer-form-full" data-testid={`${prefix}-disciplines`}>
          <FieldLabel label={editing ? "Discipline" : "Discipline (one or more)"} />
          <div className="service-kind-options">
            {DISCIPLINES.map((type) => (
              <label key={type} className={`service-kind-option ${disciplines.includes(type) ? "selected" : ""}`}>
                <input data-testid={`${prefix}-discipline-${type.toLowerCase().replace(/[^a-z]+/g, "-")}`} type={editing ? "radio" : "checkbox"} name={`${prefix}-discipline`} checked={disciplines.includes(type)} onChange={(event) => toggleDiscipline(type, event.target.checked)} />
                {type}
              </label>
            ))}
          </div>
          {multi && <small className="field-hint" data-testid={`${prefix}-disciplines-hint`}>Creates {disciplines.length} linked requests, one per discipline, each with its own provider, due date, rate and report. They share this consent record.</small>}
        </fieldset>
        {!multi && disciplines.length === 1 && (
          <label className="drawer-form-full">
            <FieldLabel label="Type" />
            <select data-testid={`${prefix}-evaluation_type`} required value={typeValue} onChange={(event) => pickType(event.target.value)}>
              <option value="">Select the {form.service_kind === "Screening" ? "screening" : form.service_kind === "Other" ? "service" : "evaluation"}</option>
              <option value="custom">Custom rate…</option>
              {typeChoices.map((option) => <option key={option.id} value={`item:${option.id}`}>{option.name}</option>)}
            </select>
            {typeChoices.length === 0 && <small className="field-hint" data-testid={`${prefix}-no-lines-hint`}>{form.client_id ? `No ${form.service_kind ? form.service_kind.toLowerCase() : ""} line for ${disciplines[0]} on this client’s rate list — use Custom rate…` : "Choose the client first."}</small>}
          </label>
        )}
        {!multi && form.rate_item_id === "custom" && (
          <div className="custom-rate-fields drawer-form-full" data-testid={`${prefix}-custom-rate`}>
            <label>
              <FieldLabel label="Service name" />
              <input data-testid={`${prefix}-custom-name`} required placeholder="e.g. AT eval" value={customRate.custom_name} onChange={(event) => setCustomRate({ ...customRate, custom_name: event.target.value })} />
            </label>
            {canSeeFees && (
              <>
                <label>
                  <FieldLabel label="Client fee" />
                  <input data-testid={`${prefix}-custom-client-fee`} type="number" min="0" step="1" value={customRate.client_fee} onChange={(event) => setCustomRate({ ...customRate, client_fee: event.target.value, provider_fee: event.target.value === "" ? "" : String(Math.round(Number(event.target.value) * 0.7)) })} />
                </label>
                <label>
                  <FieldLabel label="Provider fee" />
                  <input data-testid={`${prefix}-custom-provider-fee`} type="number" min="0" step="1" value={customRate.provider_fee} onChange={(event) => setCustomRate({ ...customRate, provider_fee: event.target.value })} />
                  <small className="field-hint">Defaults to the assigned provider’s own rate card, then 70% of the client fee. Editable.</small>
                </label>
                <label className="consent-checkbox drawer-form-full">
                  <input data-testid={`${prefix}-custom-save`} type="checkbox" checked={customRate.save_to_rate_list} onChange={(event) => setCustomRate({ ...customRate, save_to_rate_list: event.target.checked })} />
                  Save to this client’s rate list
                </label>
              </>
            )}
            {!canSeeFees && <small className="field-hint drawer-form-full">Finance sets the fee for this service.</small>}
          </div>
        )}
        {mode === "staff" && (
          <label>
            <FieldLabel label="Meeting date" optional />
            <input data-testid={`${prefix}-meeting_date`} type="date" value={form.meeting_date} onChange={(event) => { const meeting = event.target.value; setForm((current) => ({ ...current, meeting_date: meeting, due_date: meeting ? meetingDue(meeting) : current.due_date, due_date_override_note: "" })); }} />
            <small className="field-hint" data-testid={`${prefix}-meeting-hint`}>{form.meeting_date ? "Report due date set to 7 days before the meeting. Changing Needed by to another date needs an override note (logged)." : "Sets Needed by to 7 days before the meeting."}</small>
          </label>
        )}
        <Field label="Needed by" name="due_date" type="date" optional form={form} onChange={onChange} prefix={prefix} />
        {mode === "staff" && form.meeting_date && form.due_date && form.due_date !== meetingDue(form.meeting_date) && (
          <label className="drawer-form-full">
            <FieldLabel label="Override note (internal, required)" />
            <input data-testid={`${prefix}-due_date_override_note`} required value={form.due_date_override_note} onChange={(event) => onChange("due_date_override_note", event.target.value)} placeholder={`Why the report is not due ${meetingDue(form.meeting_date)} (7 days before the meeting)`} />
          </label>
        )}
        <fieldset className="consent-field drawer-form-full" data-testid={`${prefix}-consent-field`}>
          <FieldLabel label="Consent" />
          <div className="service-kind-options">
            {[["signed_form", "Signed form"], ["verbal", "Verbal from school"], ["not_yet", "Not yet"]].map(([value, label]) => (
              <label key={value} className={`service-kind-option ${consent.method === value ? "selected" : ""}`}>
                <input data-testid={`${prefix}-consent-${value.replace("_", "-")}`} type="radio" name={`${prefix}-consent`} value={value} checked={consent.method === value} onChange={() => { setConsent({ ...consent, method: value }); if (value !== "signed_form") setConsentFile(null); }} />
                {label}
              </label>
            ))}
          </div>
          {consent.method === "signed_form" && (
            <label className={`consent-upload ${consentFile || existingConsent ? "has-file" : ""}`}>
              {consentFile || existingConsent ? <FileCheck2 size={15} /> : <Paperclip size={15} />}
              <span data-testid={`${prefix}-consent-file-name`}>
                {consentFile ? consentFile.name : existingConsent ? `${existingConsent.original_filename} · replace` : "Upload signed consent (PDF or image) · required"}
              </span>
              <input accept={CONSENT_ACCEPT} data-testid={`${prefix}-consent-file`} type="file" onChange={(event) => setConsentFile(event.target.files?.[0] || null)} />
            </label>
          )}
          {consent.method === "verbal" && (
            <div className="custom-rate-fields" data-testid={`${prefix}-consent-verbal`}>
              <label>
                <FieldLabel label="Who gave it" />
                <input data-testid={`${prefix}-consent-given-by`} required value={consent.given_by} onChange={(event) => setConsent({ ...consent, given_by: event.target.value })} placeholder="Name and role at the school" />
              </label>
              <label>
                <FieldLabel label="Date" />
                <input data-testid={`${prefix}-consent-date`} required type="date" value={consent.date} onChange={(event) => setConsent({ ...consent, date: event.target.value })} />
              </label>
              <label className="drawer-form-full">
                <FieldLabel label="Note" optional />
                <input data-testid={`${prefix}-consent-note`} value={consent.note} onChange={(event) => setConsent({ ...consent, note: event.target.value })} placeholder="How consent was given" />
              </label>
            </div>
          )}
          {consent.method === "not_yet" && <small className="field-hint" data-testid={`${prefix}-consent-not-yet-hint`}>The request waits in Awaiting consent until consent is recorded.</small>}
        </fieldset>
        <Field label="Notes" name="evaluation_request" placeholder={NOTES_PLACEHOLDER} multiline optional full rows="4" form={form} onChange={onChange} prefix={prefix} />
        <button className="more-detail-toggle drawer-form-full" data-testid={`${prefix}-more-detail-toggle`} type="button" aria-expanded={showMore} onClick={() => setShowMore((current) => !current)}>
          {showMore ? <ChevronDown size={15} /> : <ChevronRight size={15} />} Add more detail
          <span>Key considerations, eligibility, contacts, consent date</span>
        </button>
        {showMore && (
          <div className="more-detail-grid drawer-form-full" data-testid={`${prefix}-more-detail`}>
            <Field label="Key considerations" name="key_considerations" multiline optional full form={form} onChange={onChange} prefix={prefix} />
            <Field label="Disability / eligibility details" name="disability" multiline optional form={form} onChange={onChange} prefix={prefix} />
            <Field label="Other support / involvement" name="other_involvement" multiline optional form={form} onChange={onChange} prefix={prefix} />
            <Field label="Teacher names & contact" name="teacher_contacts" multiline optional form={form} onChange={onChange} prefix={prefix} />
            <Field label="Parent / guardian contact" name="parent_contact" multiline optional form={form} onChange={onChange} prefix={prefix} />
            <Field label="Consent form signed date" name="consent_signed_date" type="date" optional form={form} onChange={onChange} prefix={prefix} />
          </div>
        )}
        <div className="drawer-form-full form-actions">
          <button className="button button-primary" data-testid={`submit-${prefix}-button`} disabled={isSaving} type="submit">
            <ClipboardPlus size={16} /> {isSaving ? "Saving…" : editing ? "Save changes" : mode === "client" ? "Submit evaluation request" : "Log evaluation request"}
          </button>
        </div>
      </form>
    </section>
  );
};

export const StaffEvaluationRequestForm = (props) => <EvaluationRequestForm mode="staff" {...props} />;
